/*
 * Theme provider — light / dark / system.
 *
 * Persists the user's choice in localStorage under the same per-app
 * key family the auth client uses. Applies the resolved theme to the
 * `<html>` element via two attributes so that:
 *
 *   - `data-theme="dark"` drives our own CSS variables (theme.css).
 *   - `data-bs-theme="dark"` drives Bootstrap 5.3+ component styles
 *     (form controls, dropdowns, alerts, etc.) without us having to
 *     re-declare every Bootstrap class.
 *
 * The "system" mode listens for `prefers-color-scheme` changes via
 * matchMedia, so a user who never manually toggled gets the right
 * theme even after their OS flips at sunset.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'aquastar.user.theme';

interface ThemeContextValue {
  /** What the user picked: 'light' | 'dark' | 'system'. */
  choice: ThemeChoice;
  /** Effective theme right now (system → resolved to light/dark). */
  resolved: ResolvedTheme;
  /** Set the user's preference. Persisted across reloads. */
  setChoice: (next: ThemeChoice) => void;
  /** Convenience: cycles light → dark → system. */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemPreference(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function readStoredChoice(): ThemeChoice {
  if (typeof window === 'undefined') return 'system';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private mode / disabled storage — fall through */
  }
  return 'system';
}

function applyTheme(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.setAttribute('data-bs-theme', resolved);
  // Native form controls (select, scrollbars) read this hint.
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readStoredChoice());
  const [systemPref, setSystemPref] = useState<ResolvedTheme>(() => getSystemPreference());

  // Listen for OS-level theme changes when the user is on 'system'.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      setSystemPref(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme =
    choice === 'system' ? systemPref : choice;

  // Apply the resolved theme to the document on every change.
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* swallow */
    }
  }, []);

  const toggle = useCallback(() => {
    setChoiceState((prev) => {
      const next: ThemeChoice =
        prev === 'light' ? 'dark' : prev === 'dark' ? 'system' : 'light';
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* swallow */
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolved, setChoice, toggle }),
    [choice, resolved, setChoice, toggle],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
